import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DeleteOutline,
  MailLock,
  MergeType,
  OpenInNew,
  Refresh,
  WarningAmberOutlined,
} from '@mui/icons-material';

import {
  IDuplicateEmailGroup,
  IMergeLegalClientsBody,
  useClearLegalClientEmail,
  useDeleteLegalClientHealthCheck,
  useDuplicateLegalClientEmails,
  useMergeLegalClients,
} from '../../hooks/reactQuerys/HealthCheckQueries';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { useAlert } from '../../context/AlertContext';

// Felder, die beim Merge übernommen werden dürfen (Backend-Whitelist).
type MergeField =
  | 'FirstName'
  | 'LastName'
  | 'Surname'
  | 'Birthdate'
  | 'Birthplace'
  | 'Email'
  | 'TaxNumber'
  | 'SalesTaxNumber'
  | 'idArnoTop';
const MERGE_FIELDS: { key: MergeField; label: string }[] = [
  { key: 'FirstName', label: 'Vorname' },
  { key: 'LastName', label: 'Nachname' },
  { key: 'Surname', label: 'Nachname (Geburtsname)' },
  { key: 'Birthdate', label: 'Geburtsdatum' },
  { key: 'Birthplace', label: 'Geburtsort' },
  { key: 'Email', label: 'E-Mail' },
  { key: 'TaxNumber', label: 'Steuernummer' },
  { key: 'SalesTaxNumber', label: 'USt-IdNr.' },
  { key: 'idArnoTop', label: 'ArnoTop-ID' },
];

const valueOf = (lc: ILegalClient | undefined, key: MergeField): string => {
  if (!lc) return '';
  const raw = (lc as Record<string, unknown>)[key];
  if (raw === null || raw === undefined) return '';
  return String(raw);
};

const formatDate = (iso?: string | null): string => {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('de-DE');
};

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('de-DE');
};

export const HealthCheckView: React.FC = () => {
  const { duplicateEmailGroups, isLoading, isFetching, refetch } =
    useDuplicateLegalClientEmails();

  return (
    <Box sx={{ p: 1 }}>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant='h6'>Health-Check</Typography>
          <Typography variant='body2' color='text.secondary'>
            Sucht nach Inkonsistenzen in den Stammdaten und unterstützt bei der
            Bereinigung.
          </Typography>
        </Box>
        <Tooltip title='Neu prüfen'>
          <span>
            <IconButton onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? <CircularProgress size={20} /> : <Refresh />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Card variant='outlined' sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
            <WarningAmberOutlined color='warning' />
            <Typography variant='subtitle1' fontWeight={600}>
              Doppelte E-Mail-Adressen bei Mandanten
            </Typography>
            <Chip
              size='small'
              color={duplicateEmailGroups.length === 0 ? 'success' : 'warning'}
              label={`${duplicateEmailGroups.length} Gruppe(n)`}
            />
          </Stack>
          <Typography variant='caption' color='text.secondary'>
            Jede E-Mail darf nur einem Mandanten zugeordnet sein. Dubletten
            entstehen z. B. durch fehlgeschlagene ArnoTop-Synchronisationen.
          </Typography>
        </CardContent>
      </Card>

      {isLoading ? (
        <Stack alignItems='center' sx={{ py: 4 }}>
          <CircularProgress />
        </Stack>
      ) : duplicateEmailGroups.length === 0 ? (
        <Alert severity='success'>
          Keine Dubletten gefunden — alle E-Mail-Adressen sind eindeutig.
        </Alert>
      ) : (
        duplicateEmailGroups.map(group => (
          <DuplicateGroupCard
            key={group.Email}
            group={group}
            onChanged={() => refetch()}
          />
        ))
      )}
    </Box>
  );
};

interface GroupProps {
  group: IDuplicateEmailGroup;
  onChanged: () => void;
}

const DuplicateGroupCard: React.FC<GroupProps> = ({ group, onChanged }) => {
  const { showAlert } = useAlert();
  const { mergeLegalClients, isMergingLegalClients } = useMergeLegalClients();
  const { deleteLegalClient, isDeletingLegalClient } =
    useDeleteLegalClientHealthCheck();
  const { clearLegalClientEmail, isClearingLegalClientEmail } =
    useClearLegalClientEmail();

  // Default-Target: ältester Datensatz (= erster in der Liste).
  const initialTarget = group.Members[0]?.idLegalClient ?? 0;
  const [idTarget, setIdTarget] = useState<number>(initialTarget);
  // Pro Feld: welcher idLegalClient liefert den Wert? Default = Target.
  const [fieldSource, setFieldSource] = useState<Record<string, number>>(() =>
    Object.fromEntries(MERGE_FIELDS.map(f => [f.key, initialTarget]))
  );
  const [confirmOpen, setConfirmOpen] = useState<
    'merge' | 'deleteOne' | 'clearEmail' | null
  >(null);
  const [deleteCandidate, setDeleteCandidate] = useState<ILegalClient | null>(
    null
  );
  const [clearEmailCandidate, setClearEmailCandidate] =
    useState<ILegalClient | null>(null);

  // Wenn der Target wechselt, setzen wir die Feld-Quellen ebenfalls zurück,
  // damit nicht versehentlich ein Feld aus einer alten Quelle übernommen
  // wird, die unter Umständen gelöscht werden soll.
  React.useEffect(() => {
    setFieldSource(
      Object.fromEntries(MERGE_FIELDS.map(f => [f.key, idTarget]))
    );
  }, [idTarget]);

  const target = group.Members.find(m => m.idLegalClient === idTarget);
  const sources = group.Members.filter(m => m.idLegalClient !== idTarget);

  // Felder, bei denen sich die Werte zwischen den Datensätzen unterscheiden.
  const differingFields = useMemo(
    () =>
      MERGE_FIELDS.filter(f => {
        const set = new Set(group.Members.map(m => valueOf(m, f.key)));
        return set.size > 1;
      }),
    [group.Members]
  );

  const buildOverrides = (): IMergeLegalClientsBody['fieldOverrides'] => {
    const overrides: Record<string, string | number> = {};
    for (const f of differingFields) {
      const sourceId = fieldSource[f.key] ?? idTarget;
      if (sourceId === idTarget) continue; // Target-Wert bleibt = kein Override
      const sourceClient = group.Members.find(
        m => m.idLegalClient === sourceId
      );
      const v = valueOf(sourceClient, f.key);
      if (!v) continue;
      if (f.key === 'idArnoTop') {
        const n = Number.parseInt(v, 10);
        if (!Number.isNaN(n)) overrides[f.key] = n;
      } else {
        overrides[f.key] = v;
      }
    }
    return overrides as IMergeLegalClientsBody['fieldOverrides'];
  };

  const handleAutoMerge = async () => {
    // „Daten mergen (auto)": pro differing field nehmen wir den ersten
    // nicht-leeren Wert in der Reihenfolge Target → Sources.
    const overrides: Record<string, string | number> = {};
    for (const f of differingFields) {
      const targetVal = valueOf(target, f.key);
      if (targetVal) continue; // Target hat schon was → behalten
      for (const src of sources) {
        const v = valueOf(src, f.key);
        if (v) {
          if (f.key === 'idArnoTop') {
            const n = Number.parseInt(v, 10);
            if (!Number.isNaN(n)) overrides[f.key] = n;
          } else {
            overrides[f.key] = v;
          }
          break;
        }
      }
    }
    await runMerge(overrides);
  };

  const runMerge = async (
    overrides: IMergeLegalClientsBody['fieldOverrides']
  ) => {
    try {
      await mergeLegalClients({
        idTarget,
        idSources: sources.map(s => s.idLegalClient),
        fieldOverrides: overrides,
      });
      showAlert({
        severity: 'success',
        text: `Mandanten zusammengeführt — ${sources.length} Datensatz/Datensätze gelöscht.`,
      });
      setConfirmOpen(null);
      onChanged();
    } catch (e) {
      showAlert({
        severity: 'error',
        text:
          e instanceof Error
            ? `Zusammenführen fehlgeschlagen: ${e.message}`
            : 'Zusammenführen fehlgeschlagen.',
      });
    }
  };

  const handleDeleteOne = async () => {
    if (!deleteCandidate) return;
    try {
      await deleteLegalClient(deleteCandidate.idLegalClient);
      showAlert({
        severity: 'success',
        text: `Mandant #${deleteCandidate.idLegalClient} gelöscht.`,
      });
      setConfirmOpen(null);
      setDeleteCandidate(null);
      onChanged();
    } catch (e) {
      showAlert({
        severity: 'error',
        text:
          e instanceof Error
            ? `Löschen fehlgeschlagen: ${e.message}`
            : 'Löschen fehlgeschlagen.',
      });
    }
  };

  const handleClearEmail = async () => {
    if (!clearEmailCandidate) return;
    try {
      await clearLegalClientEmail(clearEmailCandidate.idLegalClient);
      showAlert({
        severity: 'success',
        text: `E-Mail-Adresse von Mandant #${clearEmailCandidate.idLegalClient} entfernt.`,
      });
      setConfirmOpen(null);
      setClearEmailCandidate(null);
      onChanged();
    } catch (e) {
      showAlert({
        severity: 'error',
        text:
          e instanceof Error
            ? `E-Mail entfernen fehlgeschlagen: ${e.message}`
            : 'E-Mail entfernen fehlgeschlagen.',
      });
    }
  };

  const busy =
    isMergingLegalClients ||
    isDeletingLegalClient ||
    isClearingLegalClientEmail;

  return (
    <Card variant='outlined' sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
          <Typography variant='subtitle1' fontWeight={600}>
            {group.Email}
          </Typography>
          <Chip
            size='small'
            color='warning'
            label={`${group.Members.length} Datensätze`}
          />
        </Stack>

        <Table size='small' sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell padding='checkbox'>Behalten</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Geburtsdatum</TableCell>
              <TableCell>ArnoTop-ID</TableCell>
              <TableCell>Angelegt am</TableCell>
              <TableCell align='right'>Aktion</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {group.Members.map(m => {
              const isTarget = m.idLegalClient === idTarget;
              return (
                <TableRow
                  key={m.idLegalClient}
                  sx={{
                    // Helleres Pastell-Grün — `success.light` ist im DiNo-Theme
                    // recht dunkel und verschluckt den Text.
                    bgcolor: isTarget ? 'rgba(76, 175, 80, 0.18)' : undefined,
                    opacity: isTarget ? 1 : 0.85,
                  }}
                >
                  <TableCell padding='checkbox'>
                    <Radio
                      checked={isTarget}
                      onChange={() => setIdTarget(m.idLegalClient)}
                      disabled={busy}
                    />
                  </TableCell>
                  <TableCell>#{m.idLegalClient}</TableCell>
                  <TableCell>
                    {[m.FirstName, m.LastName].filter(Boolean).join(' ') || '–'}
                  </TableCell>
                  <TableCell>{formatDate(m.Birthdate)}</TableCell>
                  <TableCell>{m.idArnoTop ?? '–'}</TableCell>
                  <TableCell>{formatDateTime(m.Created_at)}</TableCell>
                  <TableCell align='right'>
                    <Stack
                      direction='row'
                      spacing={0.5}
                      justifyContent='flex-end'
                    >
                      <Tooltip title='Mandanten in neuem Tab öffnen'>
                        <IconButton
                          size='small'
                          component='a'
                          href={`/clients/${m.idLegalClient}`}
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          <OpenInNew fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Nur E-Mail-Adresse entfernen (Mandant bleibt erhalten, Public-Login wird deaktiviert)'>
                        <span>
                          <IconButton
                            size='small'
                            color='warning'
                            disabled={busy || isTarget || !m.Email}
                            onClick={() => {
                              setClearEmailCandidate(m);
                              setConfirmOpen('clearEmail');
                            }}
                          >
                            <MailLock fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title='Diesen Datensatz löschen'>
                        <span>
                          <IconButton
                            size='small'
                            color='error'
                            disabled={busy || isTarget}
                            onClick={() => {
                              setDeleteCandidate(m);
                              setConfirmOpen('deleteOne');
                            }}
                          >
                            <DeleteOutline fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {differingFields.length > 0 && (
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              mb: 2,
              bgcolor: 'background.default',
            }}
          >
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Felder zusammenführen — pro Feld den zu behaltenden Wert wählen:
            </Typography>
            <Stack spacing={1}>
              {differingFields.map(f => (
                <Stack
                  key={f.key}
                  direction='row'
                  alignItems='center'
                  spacing={1}
                >
                  <Typography
                    variant='body2'
                    sx={{ minWidth: 180, color: 'text.secondary' }}
                  >
                    {f.label}
                  </Typography>
                  <FormControl size='small' sx={{ minWidth: 240, flex: 1 }}>
                    <InputLabel id={`fs-${f.key}`}>Quelle</InputLabel>
                    <Select
                      labelId={`fs-${f.key}`}
                      label='Quelle'
                      value={fieldSource[f.key] ?? idTarget}
                      onChange={e =>
                        setFieldSource(prev => ({
                          ...prev,
                          [f.key]: Number(e.target.value),
                        }))
                      }
                      disabled={busy}
                    >
                      {group.Members.map(m => {
                        const v = valueOf(m, f.key);
                        const isTarget = m.idLegalClient === idTarget;
                        return (
                          <MenuItem
                            key={m.idLegalClient}
                            value={m.idLegalClient}
                          >
                            {isTarget ? '(Ziel) ' : ''}#{m.idLegalClient}:{' '}
                            {v || <em>leer</em>}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        <Stack direction='row' spacing={1} flexWrap='wrap'>
          <Button
            variant='contained'
            startIcon={<MergeType />}
            onClick={() => setConfirmOpen('merge')}
            disabled={busy || sources.length === 0}
          >
            Zusammenführen ({sources.length} Quelle
            {sources.length === 1 ? '' : 'n'} löschen)
          </Button>
          <Tooltip title='Übernimmt automatisch alle nicht-leeren Werte aus den Quellen, anschließend werden die Quellen gelöscht.'>
            <span>
              <Button
                variant='outlined'
                startIcon={<MergeType />}
                onClick={handleAutoMerge}
                disabled={busy || sources.length === 0}
              >
                Daten automatisch mergen
              </Button>
            </span>
          </Tooltip>
        </Stack>

        <Dialog
          open={confirmOpen === 'merge'}
          onClose={busy ? undefined : () => setConfirmOpen(null)}
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle>Mandanten zusammenführen?</DialogTitle>
          <DialogContent>
            <Alert severity='warning' sx={{ mb: 1 }}>
              <AlertTitle>Diese Aktion ist nicht umkehrbar.</AlertTitle>
              Behalten wird der Datensatz <strong>#{idTarget}</strong>. Die
              Quellen{' '}
              <strong>
                {sources.map(s => `#${s.idLegalClient}`).join(', ')}
              </strong>{' '}
              werden gelöscht. Die unten gewählten Feldwerte werden in den
              Ziel-Mandanten übernommen.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(null)} disabled={busy}>
              Abbrechen
            </Button>
            <Button
              variant='contained'
              color='warning'
              onClick={() => void runMerge(buildOverrides())}
              disabled={busy}
              startIcon={busy ? <CircularProgress size={16} /> : <MergeType />}
            >
              {busy ? 'Wird ausgeführt …' : 'Zusammenführen'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={confirmOpen === 'deleteOne'}
          onClose={busy ? undefined : () => setConfirmOpen(null)}
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle>Mandant löschen?</DialogTitle>
          <DialogContent>
            <Alert severity='warning' sx={{ mb: 1 }}>
              <AlertTitle>Diese Aktion ist nicht umkehrbar.</AlertTitle>
              Der Mandant <strong>
                #{deleteCandidate?.idLegalClient}
              </strong>{' '}
              {deleteCandidate?.FirstName} {deleteCandidate?.LastName} wird
              gelöscht (Soft-Delete). Verknüpfungen zu Vorgängen, Adressen und
              Kontaktinformationen werden mit aufgeräumt.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(null)} disabled={busy}>
              Abbrechen
            </Button>
            <Button
              variant='contained'
              color='error'
              onClick={handleDeleteOne}
              disabled={busy}
              startIcon={
                busy ? <CircularProgress size={16} /> : <DeleteOutline />
              }
            >
              {busy ? 'Wird gelöscht …' : 'Löschen'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={confirmOpen === 'clearEmail'}
          onClose={busy ? undefined : () => setConfirmOpen(null)}
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle>E-Mail-Adresse entfernen?</DialogTitle>
          <DialogContent>
            <Alert severity='warning' sx={{ mb: 1 }}>
              <AlertTitle>
                Mandant bleibt erhalten, Public-Login wird deaktiviert
              </AlertTitle>
              Die E-Mail-Adresse <strong>{clearEmailCandidate?.Email}</strong>{' '}
              wird vom Mandanten{' '}
              <strong>#{clearEmailCandidate?.idLegalClient}</strong>{' '}
              {clearEmailCandidate?.FirstName} {clearEmailCandidate?.LastName}{' '}
              entfernt. Der Datensatz inklusive Vorgangs-Verknüpfungen bleibt
              unverändert. Im Public-DiNo wird das zugehörige Konto gelöscht
              (kein Login mehr möglich).
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(null)} disabled={busy}>
              Abbrechen
            </Button>
            <Button
              variant='contained'
              color='warning'
              onClick={handleClearEmail}
              disabled={busy}
              startIcon={busy ? <CircularProgress size={16} /> : <MailLock />}
            >
              {busy ? 'Wird entfernt …' : 'E-Mail entfernen'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};
